/**
 * Initial Values Utility
 */

import {
  FieldEntityProps,
  SelectOptions,
  HiddenFields,
  ObjStrKeyStrVal,
} from '../types';

/**
 * Creates initial Formik values from field configuration.
 *
 * This function:
 * - Initializes all visible fields with empty string values
 * - Checks URL query parameters for pre-filled select values
 * - Applies query parameter values as initial values
 *
 * @param fields - Array of field entities from form data
 * @param hiddenFields - Array of hidden field definitions
 * @param selectOptions - Array of select field options
 * @returns Object with field parameter names as keys and initial values
 *
 * @example
 * const initialValues = getInitialValues(fields, hiddenFields, selectOptions);
 * // { first_name: '', last_name: '', program_type: 'Pre-selected Value', ... }
 */
export const getInitialValues = (
  fields: FieldEntityProps[],
  hiddenFields: HiddenFields[],
  selectOptions: SelectOptions[],
): ObjStrKeyStrVal => {
  const initialValues: ObjStrKeyStrVal = {};
  const selectParameterNames = selectOptions.map((opt) => opt.selection);

  // Check URL for pre-filled values
  const queryParams = new URLSearchParams(window.location.search);
  const defaultSelectValues: [string, string][] = [];

  selectParameterNames.forEach((paramName) => {
    const value = queryParams.get(paramName);
    if (value) {
      defaultSelectValues.push([paramName, value]);
    }
  });

  // Build initial values
  for (const field of fields) {
    const isHidden = hiddenFields.some(
      (h) => h.parameterName === field.parameterName,
    );

    if (!isHidden) {
      initialValues[field.parameterName] = '';
    }

    // Apply URL query parameter values
    const defaultValue = defaultSelectValues.find(
      ([name]) => name === field.parameterName,
    );
    if (defaultValue) {
      initialValues[field.parameterName] = defaultValue[1];
    }
  }

  return initialValues;
};
